import * as THREE from 'three';

/** Landmark ids from scan axes-alignment step (step 2). */
export const AXES_LANDMARK_IDS = [
  'subPatellar',
  'midPatellar',
  'frontEnd',
  'internalCondyle',
  'externalCondyle',
];

/**
 * @typedef {{ x: number, y: number, z: number }} LandmarkPoint
 * @typedef {Record<string, LandmarkPoint>} LandmarkPositionsMap
 */

/**
 * World-space landmark → mesh-local coords in the post-alignment baked frame.
 * @param {THREE.Vector3} worldPoint
 * @param {THREE.Matrix4} inverseAlignmentMatrix
 */
export function worldPointToAlignedMeshLocal(worldPoint, inverseAlignmentMatrix, out = new THREE.Vector3()) {
  return out.copy(worldPoint).applyMatrix4(inverseAlignmentMatrix);
}

/**
 * @param {Record<string, { sphere?: THREE.Object3D | null }>} selections
 * @param {THREE.Matrix4} inverseAlignmentMatrix
 * @returns {LandmarkPositionsMap}
 */
export function captureLandmarkPositionsFromSpheres(selections, inverseAlignmentMatrix) {
  /** @type {LandmarkPositionsMap} */
  const positions = {};
  const world = new THREE.Vector3();
  const local = new THREE.Vector3();

  for (const id of AXES_LANDMARK_IDS) {
    const sphere = selections[id]?.sphere;
    if (!sphere?.getWorldPosition) continue;
    sphere.getWorldPosition(world);
    worldPointToAlignedMeshLocal(world, inverseAlignmentMatrix, local);
    positions[id] = { x: local.x, y: local.y, z: local.z };
  }
  return positions;
}

/**
 * @param {THREE.Object3D} mesh
 * @param {LandmarkPoint} landmark
 * @param {THREE.Vector3} [out]
 */
export function landmarkLocalToWorld(mesh, landmark, out = new THREE.Vector3()) {
  out.set(landmark.x, landmark.y, landmark.z);
  return mesh.localToWorld(out);
}

/**
 * @param {THREE.Object3D} mesh
 * @param {LandmarkPositionsMap | null | undefined} positions
 * @param {string} id
 * @param {THREE.Vector3} [out]
 * @returns {THREE.Vector3 | null}
 */
export function getLandmarkWorld(mesh, positions, id, out = new THREE.Vector3()) {
  const p = positions?.[id];
  if (!p || !mesh?.localToWorld || !Number.isFinite(p.x)) return null;
  return landmarkLocalToWorld(mesh, p, out);
}

/**
 * Same reference as alignment origin: condyle midpoint (XZ) + mid Y between sub-patellar and front end.
 * @param {THREE.Object3D} mesh
 * @param {LandmarkPositionsMap | null | undefined} positions
 * @param {THREE.Vector3} [out]
 * @returns {THREE.Vector3 | null}
 */
export function getSocketReferenceFromLandmarks(mesh, positions, out = new THREE.Vector3()) {
  const internal = getLandmarkWorld(mesh, positions, 'internalCondyle', new THREE.Vector3());
  const external = getLandmarkWorld(mesh, positions, 'externalCondyle', new THREE.Vector3());
  const subPatellar = getLandmarkWorld(mesh, positions, 'subPatellar', new THREE.Vector3());
  const frontEnd = getLandmarkWorld(mesh, positions, 'frontEnd', new THREE.Vector3());
  if (!internal || !external || !subPatellar || !frontEnd) return null;

  out.set(
    (internal.x + external.x) * 0.5,
    (subPatellar.y + frontEnd.y) * 0.5,
    (internal.z + external.z) * 0.5,
  );
  return out;
}

/**
 * Distal X for ladder top-arc target (front end landmark), else bbox min X.
 * @param {THREE.Object3D} mesh
 * @param {LandmarkPositionsMap | null | undefined} positions
 * @param {THREE.Box3} fallbackBox
 */
export function getSocketDistalXFromLandmarks(mesh, positions, fallbackBox) {
  const front = getLandmarkWorld(mesh, positions, 'frontEnd', new THREE.Vector3());
  if (front) return front.x;
  return fallbackBox.min.x;
}

export function hasCompleteLandmarkPositions(positions) {
  if (!positions) return false;
  return AXES_LANDMARK_IDS.every((id) => {
    const p = positions[id];
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
  });
}

/**
 * Build a selections-like object for measureHelpers from saved mesh-local positions.
 * @param {THREE.Object3D} mesh
 * @param {LandmarkPositionsMap | null | undefined} positions
 */
export function landmarkPositionsToMeasurePoints(mesh, positions) {
  if (!mesh || !positions) return null;
  const proxy = {};
  for (const id of AXES_LANDMARK_IDS) {
    const p = positions[id];
    if (!p) continue;
    proxy[id] = {
      sphere: {
        getWorldPosition: (out) => landmarkLocalToWorld(mesh, p, out),
        isMesh: true,
      },
      completed: true,
    };
  }
  return proxy;
}
